import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { formatDateNL } from '@/lib/format';
import { useToast } from '@/hooks/use-toast';
import { BookOpen, Upload, Download, Pencil, Loader2, X, Save, RefreshCcw, Power } from 'lucide-react';

const FUNCTIE_OPTIONS = [
  { value: 'medewerker', label: 'Medewerker' },
  { value: 'junior', label: 'Junior' },
  { value: 'senior', label: 'Senior' },
  { value: 'teamleider', label: 'Teamleider' },
  { value: 'bedrijfsleider', label: 'Bedrijfsleider' },
];

function ModalShell({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="instructieblad-modal">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="instructieblad-modal-close" className="rounded-md p-1 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TargetPicker({ locations, locationSlugs, setLocationSlugs, functies, setFuncties, disableAllLocaties }) {
  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Locaties {locationSlugs.length === 0 && !disableAllLocaties && <span className="text-slate-400">(leeg = alle locaties)</span>}
        </label>
        <div className="max-h-32 overflow-y-auto rounded-md border border-slate-300 p-2 space-y-1">
          {locations.map((l) => (
            <label key={l.slug} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={locationSlugs.includes(l.slug)}
                onChange={() => toggle(locationSlugs, setLocationSlugs, l.slug)}
                data-testid={`instructieblad-loc-${l.slug}`}
              />
              {l.name}
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Functies <span className="text-slate-400">(leeg = alle functies)</span>
        </label>
        <div className="max-h-32 overflow-y-auto rounded-md border border-slate-300 p-2 space-y-1">
          {FUNCTIE_OPTIONS.map((f) => (
            <label key={f.value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={functies.includes(f.value)}
                onChange={() => toggle(functies, setFuncties, f.value)}
                data-testid={`instructieblad-fn-${f.value}`}
              />
              {f.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function UploadDialog({ open, onClose, locations, onUploaded, isManagerOnly }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [locationSlugs, setLocationSlugs] = useState([]);
  const [functies, setFuncties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setFile(null); setTitle(''); setLocationSlugs([]); setFuncties([]); setError('');
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!file) { setError('Selecteer een bestand'); return; }
    if (!title.trim()) { setError('Titel is verplicht'); return; }
    if (isManagerOnly && locationSlugs.length === 0) {
      setError('Kies minimaal je eigen locatie'); return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('location_slugs', JSON.stringify(locationSlugs));
    fd.append('functies', JSON.stringify(functies));
    try {
      const { data } = await api.post('/instructiebladen/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded?.(data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title="Instructieblad uploaden">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Titel</label>
          <input
            value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
            data-testid="instructieblad-upload-title"
            className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Bestand (PDF/JPG/PNG/DOCX, max 10 MB)</label>
          <input
            type="file" accept=".pdf,.jpg,.jpeg,.png,.docx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="instructieblad-upload-file"
            className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-brand-navy file:px-3 file:py-1 file:text-xs file:font-medium file:text-white"
          />
        </div>
        <TargetPicker
          locations={locations} locationSlugs={locationSlugs} setLocationSlugs={setLocationSlugs}
          functies={functies} setFuncties={setFuncties} disableAllLocaties={isManagerOnly}
        />
        {error && <p className="rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700" data-testid="instructieblad-upload-error">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
          <button
            type="submit" disabled={busy} data-testid="instructieblad-upload-submit"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Uploaden
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function EditDialog({ sheet, onClose, onSaved, locations, isManagerOnly }) {
  const [title, setTitle] = useState(sheet?.title || '');
  const [locationSlugs, setLocationSlugs] = useState(sheet?.location_slugs || []);
  const [functies, setFuncties] = useState(sheet?.functies || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setTitle(sheet?.title || '');
    setLocationSlugs(sheet?.location_slugs || []);
    setFuncties(sheet?.functies || []);
    setError('');
  }, [sheet]);

  if (!sheet) return null;

  const save = async (e) => {
    e?.preventDefault();
    if (isManagerOnly && locationSlugs.length === 0) {
      setError('Kies minimaal je eigen locatie'); return;
    }
    setBusy(true);
    setError('');
    try {
      const { data } = await api.put(`/instructiebladen/${sheet.id}`, {
        title: title.trim(), location_slugs: locationSlugs, functies,
      });
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Bijwerken mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell open={!!sheet} onClose={onClose} title="Instructieblad bewerken">
      <form onSubmit={save} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Titel</label>
          <input
            value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
            data-testid="instructieblad-edit-title"
            className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          />
        </div>
        <TargetPicker
          locations={locations} locationSlugs={locationSlugs} setLocationSlugs={setLocationSlugs}
          functies={functies} setFuncties={setFuncties} disableAllLocaties={isManagerOnly}
        />
        {error && <p className="rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
          <button
            type="submit" disabled={busy} data-testid="instructieblad-edit-submit"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export default function InstructiebladenSection({ isManagerOnly = false }) {
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sheetsResp, locResp] = await Promise.all([api.get('/instructiebladen'), api.get('/locations')]);
      setItems(sheetsResp.data.items || []);
      setLocations(locResp.data || []);
    } catch (e) {
      console.error('[instructiebladen] load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (sheet) => {
    setBusyId(sheet.id);
    try {
      const { data } = await api.put(`/instructiebladen/${sheet.id}`, { active: !sheet.active });
      setItems((s) => s.map((d) => (d.id === sheet.id ? data : d)));
      toast({ title: data.active ? 'Instructieblad geactiveerd' : 'Instructieblad gedeactiveerd' });
    } catch (e) {
      toast({ title: 'Bijwerken mislukt', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const replaceFile = async (sheet, file) => {
    if (!file) return;
    setBusyId(sheet.id);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/instructiebladen/${sheet.id}/replace-file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setItems((s) => s.map((d) => (d.id === sheet.id ? data : d)));
      toast({ title: 'Bestand vervangen', description: data.filename });
    } catch (e) {
      toast({ title: 'Vervangen mislukt', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const download = async (sheet) => {
    try {
      const resp = await api.get(`/instructiebladen/${sheet.id}/download`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: sheet.mime_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = sheet.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast({ title: 'Download mislukt', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-3" data-testid="instructiebladen-section">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <BookOpen size={18} /> Instructiebladen
          <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{items.length}</span>
        </h3>
        <button
          onClick={() => setShowUpload(true)}
          data-testid="btn-upload-instructieblad"
          className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light"
        >
          <Upload size={14} /> Uploaden
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="instructiebladen-empty">
            <BookOpen size={32} className="mx-auto mb-2 text-slate-300" />
            <p>Nog geen instructiebladen geüpload.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="py-2 px-3">Titel / Bestand</th>
                  <th className="py-2 px-3">Doelgroep</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Geüpload</th>
                  <th className="py-2 px-3 text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`instructieblad-row-${d.id}`}>
                    <td className="py-2 px-3">
                      <div className="font-medium text-slate-900">{d.title}</div>
                      <div className="text-[11px] text-slate-500">{d.filename}</div>
                    </td>
                    <td className="py-2 px-3 text-[11px] text-slate-600">
                      <div>{d.location_slugs?.length ? d.location_slugs.join(', ') : 'Alle locaties'}</div>
                      <div className="text-slate-400">{d.functies?.length ? d.functies.join(', ') : 'Alle functies'}</div>
                    </td>
                    <td className="py-2 px-3">
                      <span
                        data-testid={`instructieblad-status-${d.id}`}
                        className={`rounded px-2 py-0.5 text-[11px] font-medium ${d.active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}
                      >
                        {d.active ? 'Actief' : 'Inactief'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-[11px] text-slate-600">{formatDateNL(d.uploaded_at)}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => download(d)} data-testid={`instructieblad-download-${d.id}`} title="Download" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy">
                          <Download size={14} />
                        </button>
                        <label className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy cursor-pointer" title="Bestand vervangen">
                          <RefreshCcw size={14} />
                          <input
                            type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" className="hidden"
                            data-testid={`instructieblad-replace-${d.id}`}
                            disabled={busyId === d.id}
                            onChange={(e) => replaceFile(d, e.target.files?.[0])}
                          />
                        </label>
                        <button
                          onClick={() => toggleActive(d)} disabled={busyId === d.id}
                          data-testid={`instructieblad-toggle-${d.id}`} title={d.active ? 'Deactiveren' : 'Activeren'}
                          className="rounded-md p-1.5 text-slate-500 hover:bg-amber-50 hover:text-amber-700"
                        >
                          <Power size={14} />
                        </button>
                        <button onClick={() => setEditingSheet(d)} data-testid={`instructieblad-edit-${d.id}`} title="Bewerken" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy">
                          <Pencil size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UploadDialog
        open={showUpload} onClose={() => setShowUpload(false)} locations={locations} isManagerOnly={isManagerOnly}
        onUploaded={(sheet) => { setItems((s) => [sheet, ...s]); toast({ title: 'Instructieblad geüpload', description: sheet.title }); }}
      />
      <EditDialog
        sheet={editingSheet} onClose={() => setEditingSheet(null)} locations={locations} isManagerOnly={isManagerOnly}
        onSaved={(updated) => { setItems((s) => s.map((d) => (d.id === updated.id ? updated : d))); toast({ title: 'Instructieblad bijgewerkt' }); }}
      />
    </div>
  );
}
